import React, { useEffect, useRef, useState } from "react";
import { AppraiserProperty } from "../../types/appraiser.types";
import { ContentCard } from "../../components/ContentCard";
import { Divider, Grid, IconButton } from "@mui/material";
import { StyledImage } from "common/components/images/StyledImage";
import {
  PropertyImage,
  PropertyPhotoCategory,
  PropertyPhotoCategoryTranslations,
} from "common/types/common.types";
import { Typography } from "common/components/Typography";
import { ImageGallery } from "../../components/imageGallery/ImageGallery";
import {
  getImageWidth,
  getResponsiveImageUri,
} from "common/helpers/image.helpers";
import { usePrintableQuery, useScrollToTop } from "../../hooks/router.hooks";
import EditIcon from "@mui/icons-material/Edit";
import { PropertyPhotoCategoryDialog } from "./PropertyPhotoCategoryDialog";
import { PhotoCategoryEditDialog } from "./PhotoCategoryEditDialog";
import { useMutation, useQueryClient } from "react-query";
import {
  setAppraiserPropertyPhotoCategoryDescription,
  updatePhotoCategory,
} from "../../api/appraiserProperties.api";
import { showToastError } from "common/toast/toast";

interface PropertyGalleryProps {
  property: AppraiserProperty;
}

export const PropertyGallery: React.FC<PropertyGalleryProps> = ({
  property,
}) => {
  const windowWidthRef = useRef(window.innerWidth);
  const queryClient = useQueryClient();
  const printable = usePrintableQuery();

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [dialogCategory, setDialogCategory] = useState<PropertyPhotoCategory | null>(null);
  const [dialogCategoryDescription, setDialogCategoryDescription] =
    useState<string>("");
  const [editingPhoto, setEditingPhoto] = useState<PropertyImage | null>(null);

  const images = property.images;

  useScrollToTop();
  
  const { mutate: mutatePropertyPhotoCategoryDescription, isLoading: isLoadingPhotoCategoryDescription } =
  useMutation(async (params: Parameters<typeof setAppraiserPropertyPhotoCategoryDescription>[0]) => {
    await setAppraiserPropertyPhotoCategoryDescription(params);
    await queryClient.invalidateQueries(["appraiserProperties", property._id]);
  }, {
    onSuccess: () => {
      setDialogCategory(null);
      setDialogCategoryDescription("");
    },
    onError: () => {
      showToastError();
    },
  });

  const { mutate: mutatePhotoCategory, isLoading: isLoadingPhotoCategory } =
    useMutation(async (params: Parameters<typeof updatePhotoCategory>[0]) => {
      await updatePhotoCategory(params);
      await queryClient.invalidateQueries(["appraiserProperties", property._id]);
    }, {
      onSuccess: () => {
        setEditingPhoto(null);
      },
      onError: () => {
        showToastError();
      },
    });

  if (!property.images.length) return null;
  return (
    <ContentCard title="Galerija">
      {Object.values(PropertyPhotoCategory).map((category) => {
        const filteredImages = images.filter(
          (image) =>
            (image.category ?? PropertyPhotoCategory.Other) === category
        );

        if (filteredImages.length === 0) {
          return null;
        }

        const key = category;

        const categoryName = PropertyPhotoCategoryTranslations[category];

        const categoryDescription = property.photoCategories?.find(
          (photoCategory) => photoCategory.category === category
        )?.description;

        return (
          <div key={key}>
            {!!categoryName && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h2">{categoryName}</Typography>
                {!printable && (
                  <IconButton
                    onClick={() => {
                      setDialogCategory(category);
                      setDialogCategoryDescription(categoryDescription || "");
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </div>
            )}
            {!!categoryDescription && (
              <div style={{ marginTop: "10px" }}>
                <Typography variant="caption">{categoryDescription}</Typography>
              </div>
            )}
            <Grid
              container
              spacing={2}
              style={{
                marginTop: "10px",
              }}
            >
              {filteredImages.map((image) => (
                <Grid item xs={12} md={6} key={image._id ?? image.url}>
                  <div style={{ position: "relative" }}>
                    <div
                      onClick={() => {
                        setIsGalleryOpen(true);
                        setSelectedImageIndex(
                          images.findIndex((img) => img === image)
                        );
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <StyledImage
                        imageProps={{
                          source: { uri: image.url },
                          resizeMode: "contain",
                        }}
                        height={400}
                      />
                    </div>
                    {!printable && image._id && (
                      <IconButton
                        onClick={() => setEditingPhoto(image)}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(255,255,255,0.85)",
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </Grid>
              ))}
            </Grid>
            <Divider
              style={{
                marginTop: 30,
                marginBottom: 30,
              }}
            />
          </div>
        );
      })}
      {dialogCategory && (
        <PropertyPhotoCategoryDialog
          open={!!dialogCategory}
          onClose={() => {
            setDialogCategory(null);
            setDialogCategoryDescription("");
          }}
          category={dialogCategory}
          initialDescription={dialogCategoryDescription}
          isLoading={isLoadingPhotoCategoryDescription}
          onSubmit={(category, description) => {
            mutatePropertyPhotoCategoryDescription({propertyId: property._id, category, description});
          }}
        />
      )}
      {editingPhoto && (
        <PhotoCategoryEditDialog
          open={!!editingPhoto}
          onClose={() => setEditingPhoto(null)}
          isLoading={isLoadingPhotoCategory}
          currentCategory={editingPhoto.category ?? PropertyPhotoCategory.Other}
          onSubmit={(category: PropertyPhotoCategory) => {
            mutatePhotoCategory({
              photoId: editingPhoto._id!,
              category,
            });
          }}
        />
      )}
      <ImageGallery
        images={
          images?.map((img) =>
            getResponsiveImageUri(
              img.url,
              getImageWidth(windowWidthRef.current)
            )
          ) ?? []
        }
        isOpen={isGalleryOpen}
        onClose={() => {
          setIsGalleryOpen(false);
        }}
        headerText={property.address}
        defaultIndex={selectedImageIndex}
      />
    </ContentCard>
  );
};
